
const PictViewClass = require('pict-view');

class LIMSSampleRecordView extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('../LIMS-Application-Web').LIMSPict} */
		this.pict;

		this.ActiveSampleLogs = [];
		/** @type {Record<string, any>} */
		this.extendedSchema = null;

		this.AppData.SampleDetails = { };
		this.AppData.ActiveLIMSSampleDocuments = [];
		this.AppData.ActiveLIMSSampleLineItems = [];
		this.AppData.ActiveLIMSSampleTestInstanceBundle = [];
		this.CurrentSearchFilters = {
			PageSize: 250,
			CurrentPage: 1,
			LineItem: [],
		};
		this.deletePermission = 'Denied';
		this.ActiveLineItemID = 0;
		this.ActiveLIProjectID = 0;
		this.ActiveRepresentedQuantity = 0;
		this.lineItemAddOpen = false;

		this.manifest = require('./defaultSampleViewManifest.json');

		this.pict.MetaTemplate.addPattern('{~TestInstanceName:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpRecord = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			return this.AppData.ActiveLIMSSampleTestInstanceBundle?.TestMap?.[tmpRecord.IDTest]?.Name || tmpRecord.Name || 'N/A';
		});

		this.pict.MetaTemplate.addPattern('{~TestInstanceStatus:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpRecord = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			const status = tmpRecord.Status || tmpRecord.TestResult || 'N/A';
			const colorMap =
			{
				'pass': 'green',
				'fail': 'red',
				'sqty': 'green',
			};
			return `<span style="font-weight: bold; color: ${ colorMap[status?.toLowerCase()] || 'black' };" >${ status }</span>`;
		});

		this.pict.MetaTemplate.addPattern('{~TestInstanceAttachedDocuments:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpRecord = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			const documents = this.AppData.ActiveLIMSSampleTestInstanceBundle?.TestInstanceDocumentMap?.[tmpRecord.IDTestInstance];
			if (!documents?.length)
			{
				return '<em class="has-text-text-50">None</em>';
			}
			let outTemplate = '';
			for (const d of documents)
			{
				outTemplate += `<div><a class="is-ghost" target="_blank" href="#/Document/${ d.GUIDDocument }">${ d.Name }</a>${ this.fable.parseTemplate('{~QuickSummary:Document^Record.IDDocument~}', d) }</div>`;
			}
			return outTemplate;
		});

		this.pict.MetaTemplate.addPattern('{~AddDocumentButtonClass:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpRecord = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			// Note: this button is hidden for now.
			return this.AppData.ActiveLIMSSampleTestInstanceBundle?.TestInstanceDocumentMap?.[tmpRecord.IDTestInstance]?.length ? 'is-hidden' : 'is-hidden';
		});
	}

	/*
	 * Lifecycle hook that triggers when the view is initialized.
	 *
	 * @param {import('pict-view').ErrorCallback} fCallback
	 */
	async onInitializeAsync(fCallback)
	{
		return super.onInitializeAsync(fCallback);
	}

	/**
	 * Navigation handler
	 * @param {string} pSampleGUID - GUID for the Sample being rendered.
	 * @param {boolean} [pResetState] - Whether the component state should be reset while navigating.
	 */
	async onNavigate(pSampleGUID, pResetState = true)
	{
		this.AppData.GUIDSample = pSampleGUID;
		if (pResetState)
		{
			delete this.AppData.LoggedBy;
			//TODO: if we came here from the sample edit screen, do we want to leave this rather than re-loading?
			this.AppData.SampleDetails = { };
			this.AppData.ActiveLIMSSampleDocuments = [];
			this.AppData.ActiveLIMSSampleLineItems = [];
			this.AppData.ActiveLIMSSampleTestInstanceBundle = [];
			// A fresh sample opens on the Details tab; no heavy tab data is loaded
			// until the user actually opens that tab (see _ensureTabDataLoaded).
			this._activatedTabs = {};
			this.ActiveLIProjectID = 0;
			this.ActiveLineItemID = 0;
			this.ActiveRepresentedQuantity = 0;
			this.lineItemAddOpen = false;
		}
		if (this.initialized)
		{
			for (const pickList of this.manifest.PickLists || [])
			{
				this.pict.providers.DynamicMetaLists.rebuildListByHash(pickList.Hash);
			}
		}
		await new Promise((resolve) => this.renderAsync((pError) =>
		{
			if (pError)
			{
				this.log.error(`[Sample-Record-View] Error rendering Sample view: ${pError.message || pError}`, { stack: pError.stack });
			}
			else
			{
				this.log.info('[Sample-Record-View] Sample view rendered');
			}
			resolve();
		}));
	}

	/*
	 * Lifecycle hook that triggers before the view is rendered.
	 *
	 * @param {import('pict-view').ErrorCallback} fCallback
	 * @param {import('pict-view').Renderable} pRenderable
	 */
	async onBeforeRenderAsync(fCallback, pRenderable)
	{
		this.ActiveLineItemID = 0;
		this.ActiveRepresentedQuantity = 0;
		this.deletePermission = 'Denied';

		const session = await this.pict.providers.SessionProvider.getSession();
		if (!this.initialized || (session.CustomerID !== this.customer))
		{
			this.customer = session.CustomerID;
			// Check for a manifest from the DB before loading the default.
			const remoteManifest = await this.pict.ManifestPackagingService.loadRecordManifestSchema('Sample', 'View', this.pict.PictApplication.options.pict_configuration.Product);
			this.manifest = remoteManifest || this.manifest;
			this.pict.views.PictFormMetacontroller.clearManifestDescription();
			this.pict.views.PictFormMetacontroller.bootstrapPictFormViewsFromManifest(this.manifest);
			this.pict.views['PictSectionForm-SampleView'].viewMarshalDestination = 'AppData';
			this.pict.views['PictSectionForm-SampleView'].options.DefaultDestinationAddress = '#LIMSSampleRecordView-View-Container';
			for (const pickList of this.manifest.PickLists || [])
			{
				this.pict.providers.DynamicMetaLists.rebuildListByHash(pickList.Hash);
			}
			this.pict.views['PictSectionForm-SampleView'].rebuildCustomTemplate();
			this.initialized = true;
		}
		// Kicked off next to the record read rather than awaited inline; it settles with the rest of
		// the page's loads below.
		let tmpDeletePermissionLoad = null;
		if (this.AppData.GUIDSample)
		{
			// get the Sample record by ID
			const tmpRecord = await this.pict.providers.SampleProvider.getSampleByGUID(this.AppData.GUIDSample);
			if (tmpRecord)
			{
				this.AppData.SampleDetails = tmpRecord;
				tmpDeletePermissionLoad = Promise.resolve(this.pict.PictApplication.PermissionsControl.checkEntityPermission('Sample', this.AppData.SampleDetails, 'Delete'))
					.then((pPermission) => { this.deletePermission = pPermission; })
					// Handled here rather than by the gather below so a failed check leaves deletePermission
					// at its 'Denied' default instead of failing the whole page load.
					.catch((pError) => { this.log.error('Error resolving the Sample delete permission:', pError); });
			}
		}
		return super.onBeforeRenderAsync(async () =>
		{
			try
			{
				this.pict.views['PictSectionForm-SampleView'].rebuildCustomTemplate();
				this.log.info('Sample-Record-View rendered with Active Record', this.AppData.SampleDetails);

				// Once the Sample record is in hand nothing below depends on anything else below, so
				// they are gathered and awaited together instead of as a serial chain. They all still
				// settle before fCallback, so the render contract is unchanged.
				const tmpPageLoads = [ this.pict.PictApplication.applySampleDisabledState() ];
				if (tmpDeletePermissionLoad)
				{
					tmpPageLoads.push(tmpDeletePermissionLoad);
				}

				if (this.AppData.GUIDSample)
				{
					this.log.info('Sample Details view opened with GUIDSample', this.AppData.GUIDSample);
					if (this.AppData.SampleDetails)
					{
						this.ActiveLIProjectID = this.AppData.SampleDetails.IDProject || 0;
						tmpPageLoads.push(new Promise((resolve, reject) =>
						{
							this.pict.HeadlightRestClient.getEntitySet('Project', `FBV~IDProject~EQ~${ this.AppData.SampleDetails.IDProject }`, (err, res) =>
							{
								if (err)
								{
									return reject(err);
								}
								resolve(res);
							});
						}).then((pProjects) => { this.CurrentSearchFilters.Project = pProjects; }));
						// Resolve spec-year scope so downstream picker providers
						// (e.g. MaterialSelector) can apply contract-scoped filters
						// on their first AJAX search. Resolver fails open per §5.1
						// of the spec-year design doc if the contract has no FK,
						// the set is empty, or the customer has no spec sets.
						if (this.pict.providers.SpecScopeResolver && this.ActiveLIProjectID > 0)
						{
							tmpPageLoads.push(this.pict.providers.SpecScopeResolver.loadForProject(this.ActiveLIProjectID));
						}
						this.log.info('Sample Details view opened with Sample record', this.AppData.SampleDetails);
						// Load only the data for tabs the user has actually opened. The
						// default Details tab needs none of this, so a fresh sample open
						// fires zero of these heavy fan-outs. Each heavy tab loads its
						// data the first time it is activated (see _ensureTabDataLoaded);
						// the flags survive renderAsync refreshes (reset only on navigate).
						this._activatedTabs = this._activatedTabs || {};
						if (this._activatedTabs.Documents)
						{
							tmpPageLoads.push(this.pict.providers.DocumentProvider.getDocumentsByRemoteID(this.AppData.SampleDetails.IDSample, 'Sample')
								.then((pDocuments) => { this.AppData.ActiveLIMSSampleDocuments = pDocuments; }));
						}
						if (this._activatedTabs.LineItems)
						{
							tmpPageLoads.push(this.pict.providers.LineItemProvider.getLineItemsByIDSample(this.AppData.SampleDetails.IDSample)
								.then((pLineItems) => { this.AppData.ActiveLIMSSampleLineItems = pLineItems; }));
						}
						if (this._activatedTabs.TestInstances)
						{
							tmpPageLoads.push(this.pict.providers.TestInstanceProvider.getTestInstanceBundleForSample(this.AppData.SampleDetails.IDSample)
								.then((pBundle) => { this.AppData.ActiveLIMSSampleTestInstanceBundle = pBundle; }));
						}
						// Sample logs stay eager — a single cheap request, and the add-log
						// flow (showSampleDetailView) depends on them being present.
						tmpPageLoads.push(this.pict.providers.SampleLogProvider.getSampleLogsByIDSample(this.AppData.SampleDetails.IDSample)
							.then((pLogs) => { this.ActiveSampleLogs = pLogs; }));
					}
					else
					{
						//FIXME: should notify the user / show a message / render a template ...
						this.log.error('Sample record not found for ID', this.AppData.GUIDSample);
					}
				}
				await Promise.all(tmpPageLoads);
			}
			catch (error)
			{
				this.log.error('Error in Sample Details onBeforeRenderAsync:', error);
			}
			fCallback();
		}, pRenderable);
	}

	/*
	 * Lifecycle hook that triggers after the view is rendered.
	 *
	 * @param {import('pict-view').ErrorCallback} fCallback
	 * @param {import('pict-view').Renderable} pRenderable
	 */
	onAfterRenderAsync(fCallback, pRenderable)
	{
		this.pict.views['PictSectionForm-SampleView'].render();
		this.pict.views['PictSectionForm-SampleView'].marshalToView();
		if (this.AppData.disableSampleEdit)
		{
			document.getElementById('LIMSSampleRecordPanel-Edit')?.classList?.add?.('is-hidden');
		}
		if (this.AppData.disableSampleCreate)
		{
			document.getElementById('LIMSSampleRecordPanel-Clone')?.classList?.add?.('is-hidden');
		}
		this.initializeSelect2Plugin({
			placeholder: 'Select a Project..',
			ElementID: 'LIMSSampleRecordView-ProjectSelector',
			ParentID: 'LIMSSampleRecordView-ProjectSelectorParent',
			URL: 'Projects',
			IDField: 'IDProject',
			TextField: [ 'Name' ],
		}, () =>
		{
			this.log.info('Select2 LineItems initialized');
		});
		this.initializeSelect2Plugin({
			IDProject: this.pict.AppData.SampleDetails.IDProject,
			placeholder: 'Select a Line Item..',
			ElementID: 'LIMSSampleRecordView-LineItemSelector',
			ParentID: 'LIMSSampleRecordView-LineItemSelectorParent',
			URL: 'LineItems',
			IDField: 'IDLineItem',
			TextField: [ 'Name' ],
		}, () =>
		{
			this.log.info('Select2 Projects initialized');
		});
		if (this.deletePermission == 'Allowed')
		{
			document.getElementById('LIMSSampleRecordPanel-Details-Delete-Button')?.classList?.remove?.('is-hidden');
		}
		return super.onAfterRenderAsync(fCallback, pRenderable);
	}

	/**
	 * Initializes a Select2 Selector via jQuery
	 * @param {object} pOptions - The select customization options.
	 * @param {function} pCallback - Callback for when initialization is complete.
	 */
	initializeSelect2Plugin(pOptions, pCallback)
	{
		$('#' + pOptions.ElementID).select2({
			dropdownParent: $('#' + pOptions.ParentID),
			dropdownCssClass: pOptions.dropdownCssClass || 'theme-light',
			/* @ts-ignore */
			selectionCssClass: pOptions.selectionCssClass || 'theme-light',
			placeholder: pOptions.placeholder || 'Select...',
			minimumInputLength: pOptions.minimumInputLength > -1 ? pOptions.minimumInputLength : this.pict.views.HeadlightPanels.MinimumInputLengthForSearch,
			language: {
				searching: function()
				{
					return pOptions.searchingMessage || 'Searching...';
				},
				noResults: function()
				{
					return pOptions.noResultsMessage || 'No results found.';
				},
				loadingMore: function()
				{
					return pOptions.loadingMoreMessage || 'Loading more results...';
				},
				inputTooShort: function(args)
				{
					return pOptions.inputTooShortMessage || 'Please enter at least ' + args.minimum + ' characters to see results.';
				},
			},
			ajax: {
				xhrFields: {
					withCredentials: true,
				},
				dataType: 'json',
				method: 'GET',
				url: function(params)
				{
					const tmpSearchQuery = {
						filters: [],
						pagination: {
							start: pOptions?.CurrentPage || 1,
							limit: pOptions?.PageSize || 250,
						},
					};
					if (pOptions.URL == 'LineItems')
					{
						tmpSearchQuery.filters.push({
							field: 'IDProject',
							comparator: 'EQ',
							target: _Pict.views.LIMSSampleRecordView.ActiveLIProjectID || pOptions.IDProject,
						});
					}
					// add the search term to the query if it exists
					if (params.term)
					{
						tmpSearchQuery.filters.push({
							field: '',
							target: '',
							comparator: 'FOP',
						});
						tmpSearchQuery.filters.push({
							field: 'Name',
							comparator: 'LK',
							target: params.term,
						});
						tmpSearchQuery.filters.push({
							field: '',
							target: '',
							comparator: 'FCP',
						});
					}
					const startCount = (tmpSearchQuery.pagination.start - 1) * tmpSearchQuery.pagination.limit;
					const endCount = tmpSearchQuery.pagination.start * tmpSearchQuery.pagination.limit;
					return `/1.0/${pOptions.URL}/${_Pict.providers.UtilityProvider.generateMeadowFilter(tmpSearchQuery)}/${startCount}/${endCount}`;
				},
				processResults: function(data, params)
				{
					params.page = params.page || 1;

					const tmpSelections = _Pict.views.LIMSSampleRecordView.CurrentSearchFilters[pOptions.URL] || [];
					if (data.length == 1 && params.page == 1 && pOptions.URL == 'LineItems' && !_Pict.views.LIMSSampleRecordView.ActiveLineItemID)
					{
						_Pict.views.LIMSSampleRecordView.ActiveLineItemID = data[0].IDLineItem;
					}

					return {
						results: _Pict.providers.UtilityProvider.formatSelect2Results(data, pOptions.IDField, pOptions.TextField, tmpSelections, pOptions.StickyOption, true),
						more: params.term ? data.length === _Pict.views.LIMSSampleRecordView.CurrentSearchFilters.PageSize : data.length === _Pict.views.LIMSSampleRecordView.CurrentSearchFilters.PageSize,
					};
				},
			},
		});
		// call the callback function if it exists
		if (pCallback)
		{
			pCallback();
		}
	}

	/**
	 * Open the Sample detail view for the selected Sample to view the full details
	 * @param {number|string} pIDSample - The ID of the Sample to view
	 * @param {object} pSampleRecord - The Sample record to view
	 * @param {string} pOpenTab - The tab to open when the Sample detail view is shown
	 * @return {Promise<void>}
	 * @async
	 */
	async showSampleDetailView(pIDSample, pSampleRecord, pOpenTab = 'Details')
	{
		// if a Sample record is passed in, use it, otherwise get the Sample record by ID
		if (pSampleRecord)
		{
			this.AppData.SampleDetails = Array.isArray(pSampleRecord) ? pSampleRecord[0] : pSampleRecord;
		}
		else
		{
			const tmpRecord = await this.pict.providers.SampleProvider.getSampleByID(pIDSample);
			this.AppData.SampleDetails = tmpRecord;
		}
		// if the Sample record is not found, log an error and return
		if (!this.AppData.SampleDetails)
		{
			this.log.error('[Sample-Record-View] Sample record not found for ID', pIDSample);
			return;
		}

		// get the logs for the Sample
		const tmpLogs = await this.pict.providers.SampleLogProvider.getSampleLogsByIDSample(this.AppData.SampleDetails.IDSample);
		this.ActiveSampleLogs = tmpLogs;
		// render the Sample detail view
		await new Promise((resolve, reject)=>
		{
			this.renderAsync(() =>
			{
				this.log.info('[Sample-Record-View] Sample Details View Rendered with Active Record', this.AppData.SampleDetails);
				resolve();
			});
		});
	}

	/**
	 * This method is used to add a new log to the Sample and refresh the Sample Details view.
	 * @return {Promise<void>}
	 * @async
	 */
	async addNewLogToSample()
	{
		if (!this.AppData.SampleDetails)
		{
			this.log.error('[Sample-Record-View] No active Sample to add log to');
			return;
		}
		this.pict.ContentAssignment.addClass('#LIMSSampleRecordPanel-Logs-Add-Button', 'is-loading');
		this.log.info('[Sample-Record-View] Adding new log to Sample ID:', this.AppData.SampleDetails.IDSample);

		this.log.info('[Sample-Record-View] Attempting to fetch geolocation...');
		const geoCoords = await this.pict.providers.GeoProvider.getGeoCoords();

		if (geoCoords)
		{
			// If we got a geolocation, update the sampel with it.
			await this.pict.providers.SampleProvider.updateSample(
				{
					IDSample: this.AppData.SampleDetails.IDSample,
					CaptureLatitude: geoCoords.CaptureLatitude,
					CaptureLongitude: geoCoords.CaptureLongitude,
				},
			);
			this.AppData.SampleDetails.CaptureLatitude = geoCoords.CaptureLatitude;
			this.AppData.SampleDetails.CaptureLongitude = geoCoords.CaptureLongitude;
		}

		const tmpLogMessage = this.pict.ContentAssignment.readContent('#LIMSSampleRecordView-Tab-Logs-CreateNewLog-Textarea');
		const tmpOptions = {
			IDSample: this.AppData.SampleDetails.IDSample,
			CaptureLatitude: this.AppData.SampleDetails.CaptureLatitude,
			CaptureLongitude: this.AppData.SampleDetails.CaptureLongitude,
			Notes: tmpLogMessage,
		};
		// go make the new log entry, then refresh the view
		await this.pict.providers.SampleLogProvider.createSampleLog(tmpOptions);
		this.log.info('[Sample-Record-View] Sample Log added to Sample', this.AppData.SampleDetails.IDSample);
		// NOTE: can't go back to "Logs" since we're already on "Logs" tab
		await this.showSampleDetailView(this.AppData.SampleDetails.IDSample, null, 'Logs');
		// a bit hacky to get back to the same tab again, but limited time right now
		document.getElementById('LIMSSampleRecordPanel-Logs-Tab').click();
	}

	/**
	 * Handler that is called when a document gets attached to the Sample.
	 * @param {number} IDDocument - The document ID being attached.
	 */
	async documentAttachHandler(IDDocument)
	{
		await this.pict.providers.DocumentProvider.addRemoteToDocument(IDDocument, this.AppData.SampleDetails.IDSample, 'Sample');
		this.renderAsync(()=>
		{
			this.pict.views.HeadlightPanels.handlePanelToggle('Documents', 'close');
			this.pict.views.LIMSSampleRecordView.toggleTabContent({}, 'LIMSSampleRecordPanel-Documents');
		});
	}

	/**
	 * Opens the document panel to allow attaching a document to the Sample.
	 */
	attachDocument()
	{
		this.pict.views.HeadlightPaneDocuments.Intent =
		{
			type: 'add',
			handler: (IDDocument) =>
			{
 this.documentAttachHandler(IDDocument);
			},
			afterRenderHandler: () =>
			{
				// If the sample has a project, lock the project selector down.
				if (this.AppData.SampleDetails.IDProject)
				{
					this.pict.views.DocumentCreate.CurrentDocumentRecord.Project = this.AppData.SampleDetails.IDProject;
					const stuffToHide = [ 'DocumentCreate-Entry-Project-Wrapper', 'DocumentCreate-ClearButton' ];
					for (const s of stuffToHide)
					{
						document.getElementById(s)?.classList?.add?.('is-hidden');
					}
				}
			},
		};
		this.pict.views.HeadlightPanels.handlePanelToggle('Documents', 'open');
	}

	/**
	 * Toggles the collapsible section for adding a LineItem open and closed.
	 */
	toggleLineItemAdd()
	{
		this.lineItemAddOpen = !this.lineItemAddOpen;
		if (this.lineItemAddOpen)
		{
			document.getElementById('LIMSSampleRecordView-Tab-LineItems-Caret-Icon').classList.add('fa-caret-up');
			document.getElementById('LIMSSampleRecordView-Tab-LineItems-Caret-Icon').classList.remove('fa-caret-down');
			document.getElementById('LIMSSampleRecordView-Tab-LineItems-Add-Content').classList.remove('is-hidden');
		}
		else
		{
			document.getElementById('LIMSSampleRecordView-Tab-LineItems-Caret-Icon').classList.remove('fa-caret-up');
			document.getElementById('LIMSSampleRecordView-Tab-LineItems-Caret-Icon').classList.add('fa-caret-down');
			document.getElementById('LIMSSampleRecordView-Tab-LineItems-Add-Content').classList.add('is-hidden');
		}
	}

	/**
	 * Opens a modal that allows for editing the Represented Quantity of a LineItem attached to the Sample.
	 * @param {string} GUIDSampleLineItemJoin - The GUID of the join being updated.
	 * @param {number} IDLineItem - ID of the LI in the join.
	 */
	async updateQuantityForItem(GUIDSampleLineItemJoin, IDLineItem)
	{
		const li = await new Promise((resolve, reject) => 
		{
			this.pict.HeadlightRestClient.getEntity('LineItem', IDLineItem, (err, res) =>
			{
				if (err)
				{
					return reject(err);
				}
				resolve(res);
			});
		});
		this.fable.HeadlightModal.spawnModal({
			title: `Update Quantity for ${ li.Name || 'Line Item' }`,
			message: '',
			style: 'wide',
			confirmText: 'Update',
			confirmColor: 'is-success',
			confirmIcon: 'check',
			modalBodyTemplate: /*html*/`
				<div class="container column is-12">
					<div class="mr-2" style="width: 100%;">
						<input class="is-hidden" id="LIMSUpdateQuantity-ID" value="${ GUIDSampleLineItemJoin }" />
						<div class="field">
							<label class="label block" for="LIMSUpdateQuantity-Modal">New Represented Quantity:</label>
							<div class="control">
								<input 
									id="LIMSUpdateQuantity-Modal" 
									class="input" 
									type="number"
									placeholder="0"
									autocomplete="off"
								/>
							</div>
						</div>
					</div>
				</div>`,
		}, async ()=>
		{
			this.fable.HeadlightModal.addLoader('Updating Line Item...');
			// @ts-ignore
			const newQuantity = Number(document.getElementById('LIMSUpdateQuantity-Modal')?.value) || 0;

			await _Pict.providers.LineItemProvider.saveSampleLineItemJoin(
				{
					// @ts-ignore
					GUIDSampleLineItemJoin: document.getElementById('LIMSUpdateQuantity-ID').value,
					RepresentedQuantity: newQuantity,
				},
			);
			await _Pict.views.LIMSSampleRecordView.updateSampleQuantityFromLineItems();
			_Pict.views.LIMSSampleRecordView.renderAsync(()=>
			{
				_Pict.views.LIMSSampleRecordView.toggleTabContent({ currentTarget: document.getElementById('LIMSSampleRecordPanel-LineItems-Tab') }, 'LIMSSampleRecordPanel-LineItems');
				this.fable.HeadlightModal.dismissLoader();
			});
		});
	}

	/**
	 * Removes a LineItem from the Sample
	 * @param {number} IDSampleLineItemJoin - The join id for the join being removed.
	 */
	async removeSampleLineItemJoin(IDSampleLineItemJoin)
	{
		this.fable.HeadlightModal.addLoader('Removing Line Item...');
		await this.pict.providers.LineItemProvider.deleteSampleLineItemJoin(IDSampleLineItemJoin);
		await this.updateSampleQuantityFromLineItems();
		this.renderAsync(() =>
		{
			_Pict.views.LIMSSampleRecordView.toggleTabContent({ currentTarget: document.getElementById('LIMSSampleRecordPanel-LineItems-Tab') }, 'LIMSSampleRecordPanel-LineItems');
			this.fable.HeadlightModal.dismissLoader();
		});
	}

	/**
	 * Updates the Sample Quantity by summing up the total quantities for all attached LineItems.
	 */
	async updateSampleQuantityFromLineItems()
	{
		this.AppData.ActiveLIMSSampleLineItems = await this.pict.providers.LineItemProvider.getLineItemsByIDSample(this.pict.AppData.SampleDetails.IDSample);
		let quantity = 0;
		for (const li of this.AppData.ActiveLIMSSampleLineItems)
		{
			quantity += Number(li.RepresentedQuantity) || 0;
		}
		await this.pict.providers.SampleProvider.updateSample(
			{
				IDSample: this.pict.AppData.SampleDetails.IDSample,
				GUIDSample: this.pict.AppData.SampleDetails.GUIDSample,
				RepresentedQuantity: quantity,
			},
		);
	}

	/**
	 * Updates the "selected" LineItem that will be added to the Sample. Triggered by the LineItem selector.
	 * @param {number} value - Selected IDLineItem
	 */
	updateIDLineItem(value)
	{
		this.ActiveLineItemID = value;
	}

	/**
	 * Updates the "selected" Project that filters the LineItem. Triggered by the Project selector.
	 * @param {number} value - Selected IDProject
	 */
	updateIDProject(value)
	{
		this.ActiveLIProjectID = value;
	}

	/**
	 * Updates the Quantity that will be added the LineItem join when it is added to the Sample.
	 * @param {number} value - Manually entered Represented Quantity.
	 */
	updateLineItemRepresentedQuantity(value)
	{
		this.ActiveRepresentedQuantity = value;
	}

	/**
	 * Adds the selected LineItem to the Sample.
	 * @return {Promise<void>}
	 */
	async addLineItem()
	{
		if (!this.ActiveLineItemID)
		{
			this.pict.views.HeadlightPanels.toast({
				message: 'No Line Item was selected.',
				type: 'is-danger',
			});
			return;
		}
		this.fable.HeadlightModal.addLoader('Adding Line Item...');
		await this.pict.providers.LineItemProvider.saveSampleLineItemJoin(
		{
			IDSample: this.pict.AppData.SampleDetails.IDSample,
			IDLineItem: this.ActiveLineItemID,
			RepresentedQuantity: this.ActiveRepresentedQuantity || 0,
		});
		await _Pict.views.LIMSSampleRecordView.updateSampleQuantityFromLineItems();
		this.renderAsync(()=>
		{
			_Pict.views.LIMSSampleRecordView.toggleTabContent({ currentTarget: document.getElementById('LIMSSampleRecordPanel-LineItems-Tab') }, 'LIMSSampleRecordPanel-LineItems');
			this.fable.HeadlightModal.dismissLoader();
		});
	}

	/**
	 * Opens the document panel to allow creating a new document that will be added to the Sample.
	 */
	createNewDocumentAndAttach()
	{
		this.pict.views.HeadlightPaneDocuments.Intent =
		{
			type: 'add',
			handler: (IDDocument) =>
			{
 this.documentAttachHandler(IDDocument);
			},
			afterRenderHandler: () =>
			{
				// If the sample has a project, lock the project selector down.
				if (this.AppData.SampleDetails.IDProject)
				{
					this.pict.views.DocumentCreate.CurrentDocumentRecord.Project = this.AppData.SampleDetails.IDProject;
					const stuffToHide = [ 'DocumentCreate-Entry-Project-Wrapper', 'DocumentCreate-ClearButton' ];
					for (const s of stuffToHide)
					{
						document.getElementById(s)?.classList?.add?.('is-hidden');
					}
				}
			},
		};
		this.pict.views.HeadlightPanels.handlePanelToggle('Documents', 'open');
		this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-DocumentPanel-Create', 'Document');
	}

	/**
	 * This method is used to clear the new log entry field in the Sample Details view.
	 * @return {void}
	 */
	clearNewLogEntry()
	{
		this.pict.ContentAssignment.assignContent('#LIMSSampleRecordView-Tab-Logs-CreateNewLog-Textarea', '');
	}

	/**
	 * This method is used to handle the Print button click for building the print view.
	 * @return {void}
	 */
	printQRCodeLabel()
	{
		if (!this.AppData.SampleDetails)
		{
			this.log.error('[Sample-Record-View] No active Sample to print QR Code for');
			return;
		}
		this.log.info('[Sample-Record-View] Printing Sample Details');
		this.pict.providers.QRCodeProvider.printDecoratedEntity('Sample', this.AppData.SampleDetails.QRCode);
	}

	/**
	 * Handles toggling between tabs.
	 * @param {object} event - The click event.
	 * @param {string} pTabID - ID of the tab being switched to.
	 */
	toggleTabContent(event, pTabID)
	{
		// clear the active class from all tabs
		const tmpTabs = document.querySelectorAll('.tabs ul li');
		tmpTabs.forEach((tab) =>
		{
			tab.classList.remove('is-active');
		});
		// set the active class on the selected tab
		const tmpActiveTab = event.currentTarget;
		if (tmpActiveTab)
		{
			tmpActiveTab?.parentElement.classList.add('is-active');
		}
		// hide all the tab content
		const tmpTabContent = document.querySelectorAll('.tab-content');
		tmpTabContent.forEach((content) =>
		{
			content.classList.remove('is-active');
		});
		// show the selected tab content
		const tmpActiveTabContent = document.getElementById(`${pTabID}-Content`);
		if (tmpActiveTabContent)
		{
			tmpActiveTabContent.classList.add('is-active');
		}
		if (pTabID?.includes('Print'))
		{
			this.pict.providers.QRCodeProvider.printDecoratedEntity('Sample', this.AppData.SampleDetails.QRCode, 'LIMSSampleRecordView-PrintPreview');
		}
		// Lazy-load this tab's remote data the first time it is opened.
		this._ensureTabDataLoaded(pTabID);
	}

	/**
	 * Lazy-load a tab's remote data the first time it is activated, then
	 * re-render so the tab fills in. The Details tab (default) carries no remote
	 * data, so a fresh sample open never triggers these fetches.
	 * @param {string} pTabID - Panel id being activated (e.g. 'LIMSSampleRecordPanel-Documents').
	 */
	_ensureTabDataLoaded(pTabID)
	{
		const tmpTabDataKeys =
		{
			'LIMSSampleRecordPanel-Documents': 'Documents',
			'LIMSSampleRecordPanel-LineItems': 'LineItems',
			'LIMSSampleRecordPanel-TestInstances': 'TestInstances',
		};
		const tmpKey = tmpTabDataKeys[pTabID];
		if (!tmpKey || !this.AppData.GUIDSample)
		{
			return;
		}
		this._activatedTabs = this._activatedTabs || {};
		if (this._activatedTabs[tmpKey])
		{
			return;
		}
		this._activatedTabs[tmpKey] = true;
		// The tab is already visible at this point, so fill it with a skeleton rather than leaving it
		// blank for the length of the fetch.
		this.pict.PictApplication.showDetailLoadingShell(`#LIMSSampleRecordView-${ tmpKey }-Container`, 5, false);
		// Re-render so onBeforeRenderAsync loads this tab's data, then re-show it.
		this.renderAsync(() =>
		{
			this.toggleTabContent({ currentTarget: document.getElementById(`${ pTabID }-Tab`) }, pTabID);
		});
	}

	/**
	 * Toggles the page into Edit mode.
	 * @return {void}
	 */
	goToEditView()
	{
		if (!this.AppData.SampleDetails)
		{
			this.log.error('[Sample-Record-View] No active Sample to edit');
			return;
		}
		this.log.info('[Sample-Record-View] Going to Edit Sample view for Sample GUID:', this.AppData.SampleDetails.GUIDSample);
		this.pict.providers.LIMSRouter.router.navigate(`/Sample/${this.AppData.SampleDetails.GUIDSample}/Edit`);
	}

	/**
	 * Confirmation prompt for deleting a Sample.
	 */
	confirmDeleteSample()
	{
		// prompt a confirmation modal to do the deletion
		this.fable.HeadlightModal.spawnModal({
			title: 'Delete Sample',
			modalBodyTemplate: /*html*/`
			<div class="container column is-12">
				<p class="title is-5
				has-text-centered p-2 mb-5">
					<span class="icon">
						<i class="fa fa-fw fa-exclamation-triangle has-text-danger"></i>
					</span>
					<span> Are you sure you want to delete this Sample?</span>
				</p>
				<p class="is-6 has-text-centered">This action is irreversible and will permanently delete</p>
				<p class="has-text-centered m-3"><strong>${ this.AppData.SampleDetails.Name }</strong></p>
				<p class="is-6 has-text-centered">Please click button below to confirm deletion or close this modal.</p>
			</div>`,
			modalFooterTemplate: /*html*/`
			<div class="buttons is-right">
				<button class="button is-danger is-soft" type="button" title="Cancel Deletion" onclick="_Pict.fable.services.HeadlightModal.dismissModals()">
					<span class="icon">
						<i class="fas fa-fw fa-times" aria-hidden="true"></i>
					</span>
					<span>Cancel</span>
				</button>
				<button class="button is-danger" type="button" title="Delete Sample" onclick="_Pict.views.LIMSSampleRecordView.deleteSample()">
					<span class="icon">
						<i class="fas fa-fw fa-trash" aria-hidden="true"></i>
					</span>
					<span>Delete Sample</span>
				</button>
			</div>`,
			confirmText: 'Delete Sample',
			cancelText: 'Cancel',
		});
	}

	/**
	 * Handles deleting the current sample.
	 * @return {void}
	 */
	deleteSample()
	{
		if (!this.AppData.SampleDetails)
		{
			this.log.error('[Sample-Record-View] No active Sample to delete');
			return;
		}
		this.log.info('[Sample-Record-View] Deleting Sample ID:', this.AppData.SampleDetails.IDSample);
		this.pict.providers.SampleProvider.deleteSample(this.AppData.SampleDetails.IDSample);
		this.pict.providers.LIMSRouter.router.navigate('/PSRS/Sample/List/FilteredTo/FSF~CreateDate~DESC~0');
		this.pict.fable.services.HeadlightModal.dismissModals();
	}

	/**
	 * Handles removing a TestInstance attached to a Sample.
	 * @param {number} IDTestInstance - The Test ID being removed.
	 */
	deleteTestInstance(IDTestInstance)
	{
		if (this.pict.AppData.SampleDetails.Locked)
		{
			return;
		}
		// prompt a confirmation modal to do the deletion
		this.fable.HeadlightModal.spawnModal({
			title: 'Remove Test',
			message: 'Are you sure you want to delete this Test?',
			confirmText: 'Delete',
			confirmColor: 'is-danger',
			confirmIcon: 'trash',
		}, ()=>
		{
			_Pict.providers.TestInstanceProvider.deleteTestInstanceByID(IDTestInstance);
			_Pict.views.LIMSSampleRecordView.renderAsync(()=>
			{
				_Pict.views.LIMSSampleRecordView.toggleTabContent({ currentTarget: document.getElementById('LIMSSampleRecordPanel-TestInstances-Tab') }, 'LIMSSampleRecordPanel-TestInstances');
			});
		});
	}
}

module.exports = LIMSSampleRecordView;

module.exports.default_configuration =
{
	ViewIdentifier: 'LIMSSampleRecordView',
	DefaultRenderable: 'LIMSSampleRecordView',
	DefaultDestinationAddress: '#LIMS-Application-Container',
	CSS: /*css*/`
		.tab-content {
			display: none;
		}
		.tab-content.is-active {
			display: block;
		}`,
	Templates:
	[
		{
			Hash: 'LIMSSampleRecordView-Wrapper',
			Template: /*html*/`
			<form class="container form" data-sample-id="{~D:Record.IDSample~}" onsubmit="_Pict.views.LIMSSampleRecordView.handleFormSubmitAction(event);">
				{~T:LIMSSampleRecordView-SampleHeader:AppData.SampleDetails~}
				<!-- Sample Panel Tabs -->
				{~T:LIMSSampleRecordView-Panel-Tabs:AppData.SampleDetails~}
				<!-- LIMSSampleRecordView Content > Tab Documents View -->
				<div id="LIMSSampleRecordPanel-Documents-Content" class="tab-content">
					<div id="LIMSSampleRecordView-Documents-Container">{~T:LIMSSampleRecordView-Tab-Documents:AppData.ActiveLIMSSampleDocuments~}</div>
				</div>
				<div id="LIMSSampleRecordPanel-LineItems-Content" class="tab-content">
					<div id="LIMSSampleRecordView-LineItems-Container">{~T:LIMSSampleRecordView-Tab-LineItems:AppData.ActiveLIMSSampleLineItems~}</div>
				</div>
				<div id="LIMSSampleRecordPanel-TestInstances-Content" class="tab-content">
					<div id="LIMSSampleRecordView-TestInstances-Container">{~T:LIMSSampleRecordView-Tab-TestInstances:AppData.ActiveLIMSSampleTestInstanceBundle.TestInstances~}</div>
				</div>
				<!-- LIMSSampleRecordView Content > Tab Details View -->
				<div id="LIMSSampleRecordPanel-Details-Content" class="tab-content is-active">
					<div id="LIMSSampleRecordView-Details-Container">{~T:LIMSSampleRecordView-Tab-Details:AppData.SampleDetails~}</div>
				</div>
				<!-- LIMSSampleRecordView Content > Tab Logs View -->
				<div id="LIMSSampleRecordPanel-Logs-Content" class="tab-content">
					<div id="LIMSSampleRecordView-Logs-Container">{~T:LIMSSampleRecordView-Tab-Logs:Context[0].ActiveSampleLogs~}</div>
				</div>
				<!-- LIMSSampleRecordView Content > Tab Print View -->
				<div id="LIMSSampleRecordPanel-Print-Content" class="tab-content">
					<div id="LIMSSampleRecordView-Print-Container">{~T:LIMSSampleRecordView-Tab-Print:AppData.SampleDetails~}</div>
				</div>
			</form>`,
		},
		{
			Hash: 'LIMSSampleRecordView-NewSampleHeader',
			Template: /*html*/`<h1 class="title is-4">Create New Sample</h1>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Panel-Tabs',
			Template: /*html*/`
			<nav class="tabs is-right mt-2">
				<style>
					li.tab-spacer
					{
						width: 100%;
					}
				</style>
				<ul>
					<li>
						<a id="LIMSSampleRecordPanel-Documents-Tab" class="is-active" onclick="_Pict.views.LIMSSampleRecordView.toggleTabContent(event, 'LIMSSampleRecordPanel-Documents')">
							<span class="icon">
								<i class="fa fa-fw fa-files"></i>
							</span>
							<span class="icon-text">
								Documents
							</span>
						</a>
					</li>
					<li>
						<a id="LIMSSampleRecordPanel-LineItems-Tab" class="is-active" onclick="_Pict.views.LIMSSampleRecordView.toggleTabContent(event, 'LIMSSampleRecordPanel-LineItems')">
							<span class="icon">
								<i class="fa fa-fw fa-list-tree"></i>
							</span>
							<span class="icon-text">
								Line Items
							</span>
						</a>
					</li>
					<li>
						<a id="LIMSSampleRecordPanel-TestInstances-Tab" class="is-active" onclick="_Pict.views.LIMSSampleRecordView.toggleTabContent(event, 'LIMSSampleRecordPanel-TestInstances')">
							<span class="icon">
								<i class="fa fa-fw fa-flask-vial"></i>
							</span>
							<span class="icon-text">
								Tests
							</span>
						</a>
					</li>
					<li class="is-active">
						<a id="LIMSSampleRecordPanel-Details-Tab" class="is-active" onclick="_Pict.views.LIMSSampleRecordView.toggleTabContent(event, 'LIMSSampleRecordPanel-Details')">
							<span class="icon">
								<i class="fa fa-fw fa-info-circle"></i>
							</span>
							<span class="icon-text">
								Details
							</span>
						</a>
					</li>
					<li class="tab-spacer"></li>
					<li>
						<a id="LIMSSampleRecordPanel-Logs-Tab" onclick="_Pict.views.LIMSSampleRecordView.toggleTabContent(event, 'LIMSSampleRecordPanel-Logs')">
							<span class="icon">
								<i class="fa fa-fw fa-list-timeline"></i>
							</span>
							<span class="icon-text">
								Logs
							</span>
						</a>
					</li>
					<li>
						<a id="LIMSSampleRecordPanel-Print-Tab" onclick="_Pict.views.LIMSSampleRecordView.toggleTabContent(event, 'LIMSSampleRecordPanel-Print');">
							<span class="icon">
								<i class="fa fa-fw fa-qrcode"></i>
							</span>
							<span class="icon-text">
								Print
							</span>
						</a>
					</li>
				</ul>
			</nav>`,
		},
		{
			Hash: 'LIMSSampleRecordView-SampleHeader',
			Template: /*html*/`
			<div class="container columns has-background-text-soft is-mobile is-vcentered mb-0" style="min-height: 130px">
				<div class="column px-5">
					<p class="title is-4">{~D:Record.Name:N/A~}</p>
					<p class="subtitle is-6 has-text-text-50 mb-2">
						<span class="icon">
							<i class="fas fa-fw fa-qrcode fa-sm"></i>
						</span>
						<span class="icon-text">{~D:Record.QRCode~}</span>
					</p>
					<span class="label is-size-6 has-text-text-50 mb-0">Status:</p>
					<div class="pl-2">{~StatusPill:Record.Status~}</div>
				</div>
				<div class="column is-3" style="flex-basis: content;">
					<figure class="image is-96x96 mr-1">{~QRCodeImage:Record.QRCode^96x96^~}</figure>
				</div>
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Details',
			Template: /*html*/`
			<!-- Placeholder for dynamic section -->
			<div id="LIMSSampleRecordView-View-Container" class="app-page-gutters-for-mobile px-5 mt-2"></div>
			{~T:LIMSSampleRecordView-Tab-Details-Buttons-Read:AppData.SampleDetails~}`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-LineItems',
			Template: /*html*/`
				{~TIfAbs:LIMSSampleRecordView-Tab-LineItems-Items:Record:Record.length^>^0~}
				{~TIfAbs:LIMSSampleRecordView-Tab-LineItems-Empty:Record:Record.length^<=^0~}
			`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-LineItems-Empty',
			Template: /*html*/`
			<div class="notification empty-message m-3 is-fullwidth">
				{~T:LIMSSampleRecordView-Tab-LineItems-Add:Record~}
				<p class="has-text-centered mt-2">
					<span class="icon">
						<i class="fa fa-fw fa-info-circle"></i>
					</span>
					<span class="icon-text">No Line Items are currently attached to this sample.</span>
				</p>
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-AddLineItemsButton',
			Template: /*html*/`
				{~TIfAbs:LIMSSampleRecordView-AddLineItemsButton-Disabled:Record:Record.Locked^==^1~}
				{~TIfAbs:LIMSSampleRecordView-AddLineItemsButton-Enabled:Record:Record.Locked^!=^1~}
				`,
		},
		{
			Hash: 'LIMSSampleRecordView-AddLineItemsButton-Enabled',
			Template: /*html*/`
						<button type="button" class="button is-link" title="Add Line Items" onclick="_Pict.views.LIMSSampleRecordView.toggleLineItemAdd()">
							<span class="icon">
								<i class="fa fa-fw fa-plus"></i>
							</span>
							<span>Add Line Items</span>
							<span class="icon">
								<i id="LIMSSampleRecordView-Tab-LineItems-Caret-Icon" 
									class="fa fa-fw fa-{~TIfAbs:LIMSSampleRecordView-Tab-LineItems-Caret-Up:Record:Context[0].lineItemAddOpen^TRUE^true~}{~TIfAbs:LIMSSampleRecordView-Tab-LineItems-Caret-Down:Record:Context[0].lineItemAddOpen^FALSE^false~}"></i>
							</span>
						</button>`,
		},
		{
			Hash: 'LIMSSampleRecordView-AddLineItemsButton-Disabled',
			Template: /*html*/`
						<button type="button" class="button is-link" disabled title="Sample is locked; cannot add Line Items">
							<span class="icon">
								<i class="fa fa-fw fa-plus"></i>
							</span>
							<span>Add Line Items</span>
						</button>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-LineItems-Add',
			Template: /*html*/`
			<div class="column" style="border-bottom: 1px solid var(--bulma-border);">
				<div class="mb-2" style="display: flex; flex-direction: row;">
					<div class="ml-3 mt-2" style="width: 100%;">
						<b class="has-text-text-50">Current Sample Represented Quantity:</b>
						<b>{~D:AppData.SampleDetails.RepresentedQuantity~}</b>
					</div>
					<div class="buttons is-right mr-1">
						{~T:LIMSSampleRecordView-AddLineItemsButton:AppData.SampleDetails~}
					</div>
				</div>
				{~T:LIMSSampleRecordView-Tab-LineItems-Add-Content:Record~}
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-LineItems-Add-Content',
			Template: /*html*/`
				<div id="LIMSSampleRecordView-Tab-LineItems-Add-Content" class="{~TIfAbs:LIMSSampleRecordView-Hide-Class:Record:Context[0].lineItemAddOpen^FALSE^false~}">
					{~T:LIMSSampleRecordView-LineItemSelector~}
				</div>
			`,
		},
		{
			Hash: 'LIMSSampleRecordView-LineItemSelector',
			Template: /*html*/`
			<div style="margin-bottom: 10px; display: flex;">
				<div class="mx-2" style="width: 100%;" id="LIMSSampleRecordView-ProjectSelectorParent">
					<div class="field" style="width: 100%;">
						<label class="label block" for="LIMSSampleRecordView-ProjectSelector">Select the Line Item's Project:</label>
						<div class="control select is-fullwidth">
							<select 
								id="LIMSSampleRecordView-ProjectSelector" 
								style="width: 100%;"
								onchange="_Pict.views.LIMSSampleRecordView.updateIDProject(value)"
							>{~TS:LIMSSampleRecordView-ProjectSelector-SelectedOptions:Context[0].CurrentSearchFilters.Project~}</select>
						</div>
					</div>
				</div>
				<div class="mr-2" style="width: 100%;" id="LIMSSampleRecordView-LineItemSelectorParent">
					<div class="field" style="width: 100%;">
						<label class="label block" for="LIMSSampleRecordView-LineItemSelector">Select a Line Item to Add:</label>
						<div class="control select is-fullwidth">
							<select 
								id="LIMSSampleRecordView-LineItemSelector" 
								style="width: 100%;"
								onchange="_Pict.views.LIMSSampleRecordView.updateIDLineItem(value)"
							>{~TS:LIMSSampleRecordView-LineItemSelector-SelectedOptions:Context[0].CurrentSearchFilters.LineItem~}</select>
						</div>
					</div>
				</div>
				<div class="mr-2" style="width: 100%;">
					<div class="field">
						<label class="label block" for="LIMSSampleRecordView-LineItemRepresentedQuantity">Represented Quantity:</label>
						<div class="control">
							<input 
								id="LIMSSampleRecordView-LineItemRepresentedQuantity" 
								class="input" 
								type="number"
								placeholder="0"
								autocomplete="off"
								onchange="_Pict.views.LIMSSampleRecordView.updateLineItemRepresentedQuantity(value)"
							/>
						</div>
					</div>
				</div>
				<div style="margin-right: 10px;"><label class="label block">&nbsp;</label><button type="button" onclick="_Pict.views.LIMSSampleRecordView.addLineItem()" class="button is-rounded is-link has-text-white"><i class="fas fa-fw fa-check" style="margin-right: 5px;"></i></button></div>
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-LineItemSelector-SelectedOptions',
			Template: /*html*/`<option value="{~D:Record.IDLineItem~}" selected>{~D:Record.Name~}</option>`,
		},
		{
			Hash: 'LIMSSampleRecordView-ProjectSelector-SelectedOptions',
			Template: /*html*/`<option value="{~D:Record.IDProject~}" selected>{~D:Record.Name~}</option>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Hide-Class',
			Template: 'is-hidden',
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-LineItems-Caret-Down',
			Template: 'caret-down',
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-LineItems-Caret-Up',
			Template: 'caret-up',
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-LineItems-Items',
			Template: /*html*/`
			<div id="LIMSSampleRecordView-Tab-LineItems-Items">
				<style>
					.test-entry-row
					{
						display:flex;
						justify-content: space-between;
					}
					.test-entry
					{
						border-top: 1px solid var(--bulma-border);;
						border-bottom: 1px solid var(--bulma-border);;
					}
					.test-status
					{
						margin-right: 10vw;
					}
					.hover-red:hover
					{
						color: red !important;
					}
				</style>
				{~T:LIMSSampleRecordView-Tab-LineItems-Add:Record~}
				{~TS:LIMSSampleRecordView-Tab-LineItems-Item:Record~}
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-LineItems-Actions',
			Template: /*html*/`
				<!-- LIMS Sample record-view tab action cell (bespoke per-tab buttons; NOT the PSRS list action-cell template) -->
				<div class="test-actions">
					<ul class="buttons has-addons is-centered">
						<li>
							<a class="button is-ghost" href="javascript:void(0)" onclick="event.preventDefault(); _Pict.views.LIMSSampleRecordView.updateQuantityForItem('{~D:Record.GUIDSampleLineItemJoin~}', {~D:Record.IDLineItem~})">
								<span class="icon">
									<i class="fas fa-pencil"></i>
								</span>
								<span class="icon-text">Edit Quantity</span>
							</a>
						</li>
						<li>
							<a class="button is-ghost hover-red" href="javascript:void(0)" onclick="event.preventDefault(); _Pict.views.LIMSSampleRecordView.removeSampleLineItemJoin({~D:Record.IDSampleLineItemJoin~})">
								<span class="icon">
									<i class="fas fa-trash"></i>
								</span>
								<span class="icon-text">Remove</span>
							</a>
						</li>
					</ul>
				</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-LineItems-Item',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left is-relative p-4 test-entry">
				<div class="is-block-flex is-align-items-left m-2" style="flex-grow: 1">
					<div class="test-entry-row">
						<span class="mr-2">
							<b>{~D:Record.Name~}</b>
							{~QuickSummary:LineItem^Record.IDLineItem~}
						</span>
						<span class="test-status">
							<span class="has-text-text-50">Represented Quantity:</span> <b>{~D:Record.RepresentedQuantity~}</b>
						</span>
					</div>
					<div class="is-size-7 test-entry-row ">
						<span><b class="has-text-text-50">Project:</b> {~Entity:Project^Record.IDProject^LIMSSampleRecordView-Entity-Name^~}
						{~QuickSummary:Project^Record.IDProject~}
						</span>
					</div>
					<div class="is-size-7 test-entry-row ">
						<span>
							<span class="has-text-text-50">Created on</span>
							<span>{~DateFormatNoFallback:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~}</span>
							<span class="has-text-text-50">by</span>
							<span>{~Entity:User^Record.CreatingIDUser^LIMSSampleRecordView-Tab-Documents-Item-User^~}</span>
						</span>
					</div>
				</div>
				<div class="image">
					{~T:LIMSSampleRecordView-Tab-LineItems-Actions:Record~}
				</div>
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-LineItems-Item-User',
			Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-TestInstances',
			Template: /*html*/`
				{~TIfAbs:LIMSSampleRecordView-Tab-TestInstances-Items:Record:Record.length^>^0~}
				{~TIfAbs:LIMSSampleRecordView-Tab-TestInstances-Empty:Record:Record.length^<=^0~}
			`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-TestInstances-Empty',
			Template: /*html*/`
			<div class="notification empty-message m-3 is-fullwidth">
				<p class="has-text-centered">
					<span class="icon">
						<i class="fa fa-fw fa-info-circle"></i>
					</span>
					<span class="icon-text">No Tests are currently attached to this sample.</span>
				</p>
				{~T:LIMSSampleRecordView-AddTestsButton:AppData.SampleDetails~}
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-AddTestsButton',
			Template: /*html*/`
				{~TIfAbs:LIMSSampleRecordView-AddTestsButton-Disabled:Record:Record.Locked^==^1~}
				{~TIfAbs:LIMSSampleRecordView-AddTestsButton-Enabled:Record:Record.Locked^!=^1~}
				`,
		},
		{
			Hash: 'LIMSSampleRecordView-AddTestsButton-Enabled',
			Template: /*html*/`
				<div class="column" style="border-top: 1px solid var(--bulma-border);">
					<div class="buttons mt-2 is-right">
						<button type="button" class="button is-link" title="Add New Tests" onclick="_Pict.views.TestInstanceWizard.openTestInstanceWizard();">
							<span class="icon">
								<i class="fa fa-fw fa-plus"></i>
							</span>
							<span>Add Tests</span>
						</button>
					</div>
				</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-AddTestsButton-Disabled',
			Template: /*html*/`
				<div class="column" style="border-top: 1px solid var(--bulma-border);">
					<div class="buttons mt-2 is-right">
						<button type="button" class="button is-link" title="Sample is locked; tests cannot be added" disabled>
							<span class="icon">
								<i class="fa fa-fw fa-plus"></i>
							</span>
							<span>Add Tests</span>
						</button>
					</div>
				</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-TestInstances-Items',
			Template: /*html*/`
			<div id="LIMSSampleRecordView-Tab-TestInstances-Items">
				<style>
					.test-entry-row
					{
						display:flex;
						justify-content: space-between;
					}
					.test-entry
					{
						border-top: 1px solid var(--bulma-border);;
						border-bottom: 1px solid var(--bulma-border);;
					}
					.test-status
					{
						margin-right: 10vw;
					}
					.hover-red:hover
					{
						color: red !important;
					}
				</style>
				{~T:LIMSSampleRecordView-AddTestsButton:AppData.SampleDetails~}
				{~TS:LIMSSampleRecordView-Tab-TestInstances-Item:Record~}
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-TestInstances-Actions',
			Template: /*html*/`
				<!-- LIMS Sample record-view tab action cell (bespoke per-tab buttons; NOT the PSRS list action-cell template) -->
				<div class="test-actions">
					<ul class="buttons has-addons is-centered">
						<li class="{~AddDocumentButtonClass:Record~}">
							<a class="button is-ghost" href="javascript:void(0)" onclick="event.preventDefault(); ">
								<span class="icon">
									<i class="fas fa-paperclip-vertical"></i>
								</span>
								<span class="icon-text">Add to Document</span>
							</a>
						</li>
						<li>
							<a class="button is-ghost hover-red" href="javascript:void(0)" onclick="event.preventDefault(); _Pict.views.LIMSSampleRecordView.deleteTestInstance({~D:Record.IDTestInstance~})">
								<span class="icon">
									<i class="fas fa-trash"></i>
								</span>
								<span class="icon-text">Remove</span>
							</a>
						</li>
					</ul>
				</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-TestInstances-Item',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left is-relative p-4 test-entry">
				<div class="is-block-flex is-align-items-left m-2" style="flex-grow: 1">
					<div>
						<b>{~TestInstanceName:Record~}</b>
						{~QuickSummary:TestInstance^Record.IDTestInstance~}
					</div>
					<div class="is-size-7 test-entry-row">
						<span>
							<span class="has-text-text-50">Created on</span>
							<span>{~DateFormatNoFallback:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~}</span>
							<span class="has-text-text-50">by</span>
							<span>{~Entity:User^Record.CreatingIDUser^LIMSSampleRecordView-Tab-Documents-Item-User^~}</span>
						</span>
					</div>
					<div class="is-size-7 is-flex">
						<span class="has-text-text-50 pr-1">Status:</span> {~TestInstanceStatus:Record~}
					</div>
					<div class="is-size-7 is-flex">
						<span class="has-text-text-50 pr-1">Remarks:</span>
						<!-- min-width lets the rendered remark wrap inside the flex row instead of overflowing it. -->
						<div style="flex-grow: 1; min-width: 0;">{~MarkdownRemarksClamped:Record.Notes^LIMSSampleRecordView-Remarks-Empty^~}</div>
					</div>
					<div class="is-size-7">
						<b class="has-text-text-50 pr-1">Attached Documents:</b>
						<div style="display: flex; flex-direction: column;">{~TestInstanceAttachedDocuments:Record~}</div>
					</div>
				</div>
				<div class="image">
					{~T:LIMSSampleRecordView-Tab-TestInstances-Actions:Record~}
				</div>
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Remarks-Empty',
			Template: /*html*/`<span class="has-text-text-50">N/A</span>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-TestInstances-Item-User',
			Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Documents',
			Template: /*html*/`
				{~TIfAbs:LIMSSampleRecordView-Tab-Documents-Items:Record:Record.length^>^0~}
				{~TIfAbs:LIMSSampleRecordView-Tab-Documents-Empty:Record:Record.length^<=^0~}
			`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Documents-Empty',
			Template: /*html*/`
			<div class="notification empty-message m-3 is-fullwidth">
				<p class="has-text-centered">
					<span class="icon">
						<i class="fa fa-fw fa-info-circle"></i>
					</span>
					<span class="icon-text">No documents are currently attached to this sample.</span>
				</p>
				<div class="column" style="border-top: 1px solid var(--bulma-border);">
					<div class="buttons mt-2 is-right">
						<button type="button" class="button is-link is-soft" title="Attach an Existing Document" onclick="_Pict.views.LIMSSampleRecordView.attachDocument()">
							<span class="icon">
								<i class="fa fa-fw fa-paperclip-vertical"></i>
							</span>
							<span>Attach Document</span>
						</button>
						<button type="button" class="button is-link" title="Create a New Document to Attach"onclick="_Pict.views.LIMSSampleRecordView.createNewDocumentAndAttach()">
							<span class="icon">
								<i class="fa fa-fw fa-file-plus"></i>
							</span>
							<span>New Document</span>
						</button>
					</div>
				</div>
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Documents-Items',
			Template: /*html*/`
			<div id="LIMSSampleRecordView-Tab-Documents-Items">
				<style>
					.document-entry-row
					{
						display:flex;
						justify-content: space-between;
					}
					.document-entry
					{
						border-top: 1px solid var(--bulma-border);;
						border-bottom: 1px solid var(--bulma-border);;
					}
					.document-status
					{
						margin-right: 10vw;
					}
				</style>
				<div class="column" style="border-bottom: 1px solid var(--bulma-border);">
					<div class="buttons is-right">
						<button type="button" class="button is-link is-soft" title="Attach an Existing Document" onclick="_Pict.views.LIMSSampleRecordView.attachDocument()">
							<span class="icon">
								<i class="fa fa-fw fa-paperclip-vertical"></i>
							</span>
							<span>Attach Document</span>
						</button>
						<button type="button" class="button is-link" title="Create a New Document to Attach"onclick="_Pict.views.LIMSSampleRecordView.createNewDocumentAndAttach()">
							<span class="icon">
								<i class="fa fa-fw fa-file-plus"></i>
							</span>
							<span>New Document</span>
						</button>
					</div>
				</div>
				{~TS:LIMSSampleRecordView-Tab-Documents-Item:Record~}
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Documents-Actions',
			Template: /*html*/`
				<!-- LIMS Sample record-view tab action cell (bespoke per-tab buttons; NOT the PSRS list action-cell template) -->
				<div class="document-actions">
					<ul class="buttons has-addons is-centered">
						<!-- TODO: check if the entity is print-able - currently, they both are -->
						<li>
							<a class="button is-ghost" href="javascript:void(0)" onclick="event.preventDefault(); _Pict.providers.DocumentProvider.viewDocumentFile('_blank', false, {~D:Record.IDDocument~});">
								<span class="icon">
									<i class="fas fa-print"></i>
								</span>
								<span class="icon-text">Print</span>
							</a>
						</li>
						<li>
							<a class="button is-ghost" href="#/Document/{~D:Record.GUIDDocument~}" title="{~TBR:Record.Name~}">
								<span class="icon">
									<i class="fas fa-search"></i>
								</span>
								<span class="icon-text">View</span>
							</a>
						</li>
						<li>
							<a class="button is-ghost" onclick="_Pict.providers.DocumentProvider.openFormByIDDocument(event, {~D:Record.IDDocument~});" title="{~TBR:Record.Name~}">
								<span class="icon">
									<i class="fas fa-pencil"></i>
								</span>
								<span class="icon-text">Edit</span>
							</a>
						</li>
					</ul>
				</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Documents-Item',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left is-relative p-4 document-entry">
				<div class="is-block-flex is-align-items-left m-2" style="flex-grow: 1">
					<div>
						<span class="tag">#{~D:Record.SequenceNumber~}</span>&nbsp;<b>{~D:Record.Name~}</b>
						{~QuickSummary:Document^Record.IDDocument~}
					</div>
					<div class="document-entry-row is-size-7">
						<span><b class="has-text-text-50">Type:</b> {~DocumentTypeName:Record.DocumentType~}</span>
						<span class='document-status'>
							Status: <b>{~D:Record.Status~}</b>
						</span>
					</div>
					<div class="is-size-7">
						<span class="has-text-text-50">Created on</span>
						<span>{~DateFormatNoFallback:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~}</span>
						<span class="has-text-text-50">by</span>
						<span>{~Entity:User^Record.CreatingIDUser^LIMSSampleRecordView-Tab-Documents-Item-User^~}</span>
					</div>
				</div>
				<div class="image">
					{~T:LIMSSampleRecordView-Tab-Documents-Actions:Record~}
				</div>
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Documents-Item-User',
			Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
		},
		{
			Hash: 'LIMSSampleRecordView-Entity-Name',
			Template: /*html*/`{~D:Record.Name~}`,
		},
		{
			Hash: 'LIMSSampleRecordView-EditSampleButton',
			Template: /*html*/`
				<!-- Edit Sample Button -->
				{~TIfAbs:LIMSSampleRecordView-EditSampleButton-Disabled:Record:Record.Locked^==^1~}
				{~TIfAbs:LIMSSampleRecordView-EditSampleButton-Enabled:Record:Record.Locked^!=^1~}
				`,
		},
		{
			Hash: 'LIMSSampleRecordView-EditSampleButton-Enabled',
			Template: /*html*/`
				<button type="button" id="LIMSSampleRecordPanel-Edit" class="button is-link" onclick="_Pict.views.LIMSSampleRecordView.goToEditView()">
					<span class="icon">
						<i class="fa fa-fw fa-pencil"></i>
					</span>
					<span class="icon-text">Edit Sample</span>
				</button>
				`,
		},
		{
			Hash: 'LIMSSampleRecordView-EditSampleButton-Disabled',
			Template: /*html*/`
				<button type="button" id="LIMSSampleRecordPanel-Edit" class="button is-link" disabled title="Sample is locked; editing is disabled">
					<span class="icon">
						<i class="fa fa-fw fa-pencil"></i>
					</span>
					<span class="icon-text">Edit Sample</span>
				</button>
				`,
		},
		{
			Hash: 'LIMSSampleRecordView-DeleteSampleButton',
			Template: /*html*/`
				<!-- Delete Sample Button -->
				{~TIfAbs:LIMSSampleRecordView-DeleteSampleButton-Disabled:Record:Record.Locked^==^1~}
				{~TIfAbs:LIMSSampleRecordView-DeleteSampleButton-Enabled:Record:Record.Locked^!=^1~}
				`,
		},
		{
			Hash: 'LIMSSampleRecordView-DeleteSampleButton-Enabled',
			Template: /*html*/`
				<button type="button" id="LIMSSampleRecordPanel-Details-Delete-Button" class="button is-link is-outlined is-hidden" onclick="_Pict.views.LIMSSampleRecordView.confirmDeleteSample()">
					<span class="icon">
						<i class="fa fa-fw fa-trash"></i>
					</span>
					<span class="icon-text">Delete Sample</span>
				</button>
				`,
		},
		{
			Hash: 'LIMSSampleRecordView-DeleteSampleButton-Disabled',
			Template: /*html*/`
				<button type="button" id="LIMSSampleRecordPanel-Details-Delete-Button" class="button is-link is-outlined is-hidden" disabled title="Sample is locked; deletion is disabled">
					<span class="icon">
						<i class="fa fa-fw fa-trash"></i>
					</span>
					<span class="icon-text">Delete Sample</span>
				</button>
				`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Details-Buttons-Read',
			Template: /*html*/`
			<div class="buttons my-2 mr-6 is-right">
				{~T:LIMSSampleRecordView-DeleteSampleButton:AppData.SampleDetails~}
				<!-- Clone Sample Button -->
				<button type="button" id="LIMSSampleRecordPanel-Clone" class="button is-link" onclick="_Pict.providers.LIMSRouter.router.navigate('/Create-Sample/ClonedFrom/' + _Pict.AppData.SampleDetails.GUIDSample);">
					<span class="icon">
						<i class="fa fa-fw fa-clone"></i>
					</span>
					<span class="icon-text">Clone Sample</span>
				</button>
				{~T:LIMSSampleRecordView-EditSampleButton:AppData.SampleDetails~}
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Logs',
			Template: /*html*/`
				{~TIfAbs:LIMSSampleRecordView-Tab-Logs-Items:Record:Record.length^>^0~}
				{~TIfAbs:LIMSSampleRecordView-Tab-Logs-Empty:Record:Record.length^<=^0~}
				{~T:LIMSSampleRecordView-Tab-Logs-Entry:AppData.SampleDetails~}`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Logs-Empty',
			Template: /*html*/`
			<div class="notification empty-message m-3 is-fullwidth">
				<p class="has-text-centered">
					<span class="icon">
						<i class="fa fa-fw fa-info-circle"></i>
					</span>
					<span class="icon-text">No logs are available for this sample.</span>
				</p>
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Logs-Items',
			Template: /*html*/`
			<div id="LIMSSampleRecordView-Tab-Logs-Items">
				{~TS:LIMSSampleRecordView-Tab-Logs-Item:Record~}
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Logs-Item',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left is-relative p-4">
				<figure class="image">
					<img class="preview-image" style="max-width: 100px;" onerror="this.onerror = null; this.src='./images/no-location-data-light.png'" src="{~GoogleMapURL:Record^100x100^~}" alt="Geolocation for sample" />
				</figure>
				<div class="is-block-flex is-align-items-left m-2" style="flex-grow: 1">
					<div class="is-size-7 has-text-text-50">#{~D:Record.IDSampleLog~}</div>
					<div><b>{~D:Record.Notes~}</b></div>
					<div class="is-size-7">
						<span class="has-text-text-50">on</span>
						<span>{~DateFormatNoFallback:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~}</span>
						<span class="has-text-text-50">by</span>
						<span>{~Entity:User^Record.CreatingIDUser^LIMSSampleRecordView-Tab-Logs-Item-User^~}</span>
					</div>
				</div>
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Logs-Item-User',
			Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Logs-Entry',
			Template: /*html*/`
			<div class="column" style="border-top: 1px solid var(--bulma-border);">
				<textarea id="LIMSSampleRecordView-Tab-Logs-CreateNewLog-Textarea" style="min-height: 4rem" class="textarea" placeholder="Enter log notes here"></textarea>
				<div class="buttons mt-2 is-right">
					<button type="button" class="button is-link is-soft" title="Clear log entry" onclick="_Pict.views.LIMSSampleRecordView.clearNewLogEntry()">
						<span class="icon">
							<i class="fa fa-fw fa-refresh"></i>
						</span>
						<span>Clear</span>
					</button>
					<button type="button" class="button is-link" title="Add new log to sample"onclick="_Pict.views.LIMSSampleRecordView.addNewLogToSample()">
						<span class="icon">
							<i class="fa fa-fw fa-plus"></i>
						</span>
						<span>Add Log</span>
					</button>
				</div>
			</div>`,
		},
		{
			Hash: 'LIMSSampleRecordView-Tab-Print',
			Template: /*html*/`
			<div class="columns px-3 m-0">
				<div class="column" id="LIMSSampleRecordView-PrintPreview">
				</div>
			</div>
			<div class="columns px-3 m-0">
				<div class="column">
					<div class="buttons mt-2 is-right">
						<button type="button" class="button is-link" title="Print qrcode label" onclick="_Pict.views.LIMSSampleRecordView.printQRCodeLabel()">
							<span class="icon">
								<i class="fa fa-fw fa-print"></i>
							</span>
							<span>Print QRCode Label</span>
						</button>
					</div>
				</div>
			</div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'LIMSSampleRecordView',
			TemplateHash: 'LIMSSampleRecordView-Wrapper',
			DestinationAddress: '#LIMS-Application-Container',
			RenderType: 'replace',
		},
	],
};
